import { Color, Spacing } from '@persona-id/design-tokens';
import BriefcaseIcon from '@persona-id/design-tokens/icons/BriefcaseIcon';
import CircleCheckIcon from '@persona-id/design-tokens/icons/CircleCheckIcon';
import CodeIcon from '@persona-id/design-tokens/icons/CodeIcon';
import DocumentIcon from '@persona-id/design-tokens/icons/DocumentIcon';
import GraphIcon from '@persona-id/design-tokens/icons/GraphIcon';
import IdFrontIcon from '@persona-id/design-tokens/icons/IdFrontIcon';
import LayersIcon from '@persona-id/design-tokens/icons/LayersIcon';
import MergeIcon from '@persona-id/design-tokens/icons/MergeIcon';
import UsersCheckIcon from '@persona-id/design-tokens/icons/UsersCheckIcon';
import WebhooksIcon from '@persona-id/design-tokens/icons/WebhooksIcon';
import WorkflowsIcon from '@persona-id/design-tokens/icons/WorkflowsIcon';
import Fuse from 'fuse.js';
import { groupBy, isEqual, pullAllWith, unionWith } from 'lodash/fp';
import { ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import SearchInput from 'dashboard/components-lib/SearchInput';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import {
  API_PERMISSION_DISPLAY_CONFIG,
  ApiPermissionType,
} from 'dashboard/views/DashboardApiKeysShowView/constants';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import { Label, Paragraph } from 'design-system/components/Text';
import { ApiPermission } from 'lib/api-permissions';

const TableTitleContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${Spacing[2]};
  margin-bottom: ${Spacing[2]};
`;

const AccordionTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: auto ${Spacing[5]} 1fr;
  grid-column-gap: ${Spacing[2]};
  align-items: center;
  padding-right: ${Spacing[4]};
`;

const TableTHAccordion = styled(TableTH)`
  width: 100%;
`;

const TableTDDescription = styled(TableTD)`
  padding-left: ${Spacing[9]};
`;

const IconContainer = styled('div')`
  background: ${Color.CorePurple100};
  border-radius: ${Spacing[2]};
  padding: ${Spacing[1]} 0px;
  width: ${Spacing[5]};
  height: ${Spacing[5]};
  display: flex;
  justify-content: center;

  svg {
    color: ${Color.CorePurple500};
  }
`;

interface ApiPermissionsSection {
  key: ApiPermissionType;
  name: string;
  prefixIcon: ReactNode;
}

export const API_PERMISSION_SECTIONS: ApiPermissionsSection[] = [
  {
    key: ApiPermissionType.INQUIRY,
    name: 'Inquiries',
    prefixIcon: <IdFrontIcon />,
  },
  {
    key: ApiPermissionType.VERIFICATION,
    name: 'Verifications',
    prefixIcon: <CircleCheckIcon />,
  },
  {
    key: ApiPermissionType.ACCOUNT,
    name: 'Accounts',
    prefixIcon: <UsersCheckIcon />,
  },
  {
    key: ApiPermissionType.TRANSACTION,
    name: 'Transactions',
    prefixIcon: <MergeIcon />,
  },
  {
    key: ApiPermissionType.REPORT,
    name: 'Reports',
    prefixIcon: <DocumentIcon />,
  },
  {
    key: ApiPermissionType.CASE,
    name: 'Cases',
    prefixIcon: <BriefcaseIcon />,
  },
  {
    key: ApiPermissionType.WORKFLOW,
    name: 'Workflows',
    prefixIcon: <WorkflowsIcon />,
  },
  {
    key: ApiPermissionType.GRAPH,
    name: 'Graph',
    prefixIcon: <GraphIcon />,
  },
  {
    key: ApiPermissionType.API,
    name: 'API',
    prefixIcon: <CodeIcon />,
  },
  {
    key: ApiPermissionType.WEBHOOK,
    name: 'Webhook',
    prefixIcon: <LayersIcon />,
  },
  {
    key: ApiPermissionType.PLATFORM,
    name: 'Platform',
    prefixIcon: <WebhooksIcon />,
  },
  {
    key: ApiPermissionType.TEAM,
    name: 'Team',
    prefixIcon: <BriefcaseIcon />,
  },
];

const API_PERMISSION_MAP = groupBy(
  ([_, value]) => value.type,
  Object.entries(API_PERMISSION_DISPLAY_CONFIG),
);

export interface ApiPermissionsTableProps {
  disabled?: boolean;
  value: ApiPermission[];
  onChange: (value: ApiPermission[]) => void;
}

const ApiPermissionsTable = ({ value, disabled, onChange }: ApiPermissionsTableProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const allApiPermissions = Object.values(ApiPermission);

  const isAllPermissionSelected =
    allApiPermissions.length > 0 &&
    allApiPermissions.every((apiPermission) => value.includes(apiPermission));

  const isSomePermissionSelected =
    allApiPermissions.length > 0 &&
    allApiPermissions.some((apiPermission) => value.includes(apiPermission));

  const handleSelectAllApiPermissions = () => {
    if (isAllPermissionSelected) {
      onChange([]);
    } else {
      onChange(allApiPermissions);
    }
  };

  const ApiPermissionDisplayInfo = allApiPermissions.map((p) => ({
    permission: p,
    ...API_PERMISSION_DISPLAY_CONFIG[p],
  }));

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      keys: [
        { name: 'name', weight: 0.2 },
        { name: 'description', weight: 0.4 },
        { name: 'type', weight: 0.4 },
      ],
    };
    return new Fuse(ApiPermissionDisplayInfo, fuseOptions);
  }, [ApiPermissionDisplayInfo]);

  const filteredPermissions: Array<ApiPermission> = useMemo(
    () =>
      searchTerm !== ''
        ? fuse.search(searchTerm).map(({ item }) => item.permission)
        : allApiPermissions,
    [searchTerm, allApiPermissions, fuse],
  );

  const handleSelectPermission = (permission: ApiPermission) => {
    const isSelected = value.includes(permission);

    if (isSelected) {
      onChange(pullAllWith(isEqual, [permission], value));
    } else {
      onChange(unionWith(isEqual, value, [permission]));
    }
  };

  const apiPermissionRows = API_PERMISSION_SECTIONS.map((permissionSection) => {
    const allPermissionsPerType = filteredPermissions.filter((permission) =>
      API_PERMISSION_MAP[permissionSection.key]
        .map(([permissionName, _]) => permissionName)
        .includes(permission),
    );

    // skip empty permission section
    if (allPermissionsPerType.length === 0) return;

    const permissionChildrenRows = allPermissionsPerType.map((permission) => {
      const isChecked = value.includes(permission);
      const description = API_PERMISSION_DISPLAY_CONFIG[permission].description;

      return (
        <TableTR
          data-test={`api-permissions-table__row--${permission}`}
          id={`api-permissions-table__row--${permission}`}
          key={`api-permissions-table__row--${permission}`}
          onClick={() => handleSelectPermission(permission)}
        >
          <TableTD>
            <Checkbox
              data-test={`api-permissions-table__row--${permission}__checkbox`}
              checked={isChecked}
              disabled={disabled}
              aria-label={description}
            />
          </TableTD>
          <TableTDDescription>
            <Paragraph color='primary'>{description}</Paragraph>
          </TableTDDescription>
        </TableTR>
      );
    });

    return (
      <ApiPermissionsSection
        key={`api-permissions-table__section--${permissionSection.key}`}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        disabled={disabled}
        permissionSection={permissionSection}
        filteredPermissions={allPermissionsPerType}
        selectedPermissions={value}
        setSelectedPermissions={onChange}
        searchTerm={searchTerm}
        permissionChildrenRows={permissionChildrenRows}
      />
    );
  });

  return (
    <Card>
      <Card.Body>
        <TableTitleContainer>
          <Card.Title>API Permissions</Card.Title>
          <SearchInput
            title={null}
            handleInput={setSearchTerm}
            value={searchTerm}
            placeholder='Search by permission name or type...'
            dataTest='api-permissions-table__input--search'
          />
        </TableTitleContainer>
        <TableLayout
          showHeader
          isEmpty={filteredPermissions.length === 0}
          nullState={
            <TableNullState
              hasUsedFilters={searchTerm.length > 0}
              dataTestBlock='api-permissions-table'
            >
              <h3>Looks like there aren&#39;t any permissions for this role yet.</h3>
            </TableNullState>
          }
          headerTR={
            <>
              <TableTH data-test='api-permissions-table__row--header'>
                <Checkbox
                  data-test='api-permissions-table__row--header__checkbox--select-all'
                  id='api-permissions-table__row--header__checkbox--select-all'
                  aria-label='Select all permissions'
                  checked={isAllPermissionSelected}
                  indeterminate={!isAllPermissionSelected && isSomePermissionSelected}
                  disabled={disabled || searchTerm !== ''}
                  onClick={() => handleSelectAllApiPermissions()}
                />
              </TableTH>
              <TableTHAccordion>
                <Label size='lg' weight='bold'>
                  {' '}
                  Action{' '}
                </Label>
              </TableTHAccordion>
            </>
          }
        >
          {apiPermissionRows}
        </TableLayout>
      </Card.Body>
    </Card>
  );
};

interface ApiPermissionsSectionProps {
  filteredPermissions: ApiPermission[];
  permissionSection: ApiPermissionsSection;
  selectedPermissions: ApiPermission[];
  setSelectedPermissions: (permissions: ApiPermission[]) => void;
  disabled: boolean;
  searchTerm: string;
  permissionChildrenRows: ReactNode;
}

const ApiPermissionsSection = ({
  filteredPermissions,
  permissionSection,
  selectedPermissions,
  setSelectedPermissions,
  permissionChildrenRows,
  disabled,
  searchTerm,
}: ApiPermissionsSectionProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  useMemo(() => {
    // expand accordion when user start to type in search input
    setExpanded(searchTerm.length > 0);
  }, [searchTerm]);

  const isSelectAllChecked =
    filteredPermissions.length > 0 &&
    filteredPermissions.every((permission) => selectedPermissions.includes(permission));
  const isSelectAllIndeterminate =
    filteredPermissions.length > 0 &&
    filteredPermissions.some((permission) => selectedPermissions.includes(permission));

  const handleSelectAllPerType = () => {
    if (isSelectAllChecked) {
      setSelectedPermissions(pullAllWith(isEqual, filteredPermissions, selectedPermissions));
    } else {
      setSelectedPermissions(unionWith(isEqual, selectedPermissions, filteredPermissions));
    }
  };

  return (
    <>
      <TableTR>
        <TableTD>
          <Checkbox
            data-test={`api-permissions-table__section__checkbox_${permissionSection.key}`}
            aria-label={`Select all API permissions from ${permissionSection.key}`}
            id={`api-permissions-table__section__checkbox_${permissionSection.key}`}
            checked={isSelectAllChecked}
            indeterminate={!isSelectAllChecked && isSelectAllIndeterminate}
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelectAllPerType();
            }}
          />
        </TableTD>
        <TableTD>
          <Accordion
            dataTest={`api-permissions-table__section__accordion--${permissionSection.key}`}
            padded={false}
            showCaret={true}
            caretSize='sm'
            onTitleClick={() => setExpanded(!expanded)}
            overrideExpanded={expanded}
            title={
              <AccordionTitleContainer>
                <IconContainer>{permissionSection.prefixIcon}</IconContainer>
                <Label size='lg' weight='bold'>
                  {permissionSection.name}
                </Label>
              </AccordionTitleContainer>
            }
          />
        </TableTD>
      </TableTR>
      {expanded && permissionChildrenRows}
    </>
  );
};

export default ApiPermissionsTable;
